// Document versions: list (drawer tab), new version (minor/major, optional file), restore, and side-by-side comparison.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import type { Attribute, Doc, DocVersion, FileMeta } from '../types';
import { Field, Modal, Switch, fmtBytes, fmtDateTime, useToast } from '../ui';
import { Icon } from '../ui/Icon';
import { renderValue } from './MetaFields';

const KIND: Record<string, { label: string; icon: string }> = {
  INITIAL: { label: 'Inițială', icon: 'file' }, MANUAL: { label: 'Manuală', icon: 'edit' }, FILE: { label: 'Fișier nou', icon: 'upload' },
  OCR: { label: 'OCR', icon: 'scan' }, RESTORE: { label: 'Restaurare', icon: 'refresh' }, SIGNED: { label: 'Semnat', icon: 'signature' },
};
const fixTs = (iso?: string | null) => (iso ? iso.replace(/([+-]\d{2})$/, '$1:00') : iso);
export const nextLabel = (cur: string | undefined, major: boolean) => {
  const [a, b] = (cur || '0.1').split('.').map(Number);
  return major ? `${a + 1}.0` : `${a}.${b + 1}`;
};

// ---------------------------------------------------------------- drawer tab
export function VersionsPanel({ doc, attrs, onChanged, onView, viewingId }: {
  doc: Doc; attrs: Attribute[]; onChanged: () => void; onView: (v: DocVersion | null) => void; viewingId?: string | null;
}) {
  const toast = useToast();
  const [list, setList] = useState<DocVersion[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState<DocVersion | null>(null);
  const [compare, setCompare] = useState<[number, number] | null>(null);
  const [pick, setPick] = useState<number[]>([]);
  const load = () => api.get<DocVersion[]>(`/api/documents/${doc.id}/versions`).then(setList).catch(e => toast(e.message, 'error'));
  useEffect(() => { load(); }, [doc.id, doc.versionLabel]);
  if (!list) return <p className="muted">Se încarcă…</p>;
  const latest = list[0];
  const togglePick = (id: number) => setPick(p => p.includes(id) ? p.filter(x => x !== id) : [...p.slice(-1), id]);
  const openCompare = (a: number, b: number) => { const ia = list.findIndex(v => v.id === a), ib = list.findIndex(v => v.id === b); setCompare(ia > ib ? [a, b] : [b, a]); };
  return (
    <div className="stack" style={{ gap: 10 }}>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <span className="muted small grow">Versiunea curentă: <b className="ver-chip">v{latest?.label}</b> · {list.length} {list.length === 1 ? 'versiune' : 'versiuni'}</span>
        {pick.length === 2 && <button className="btn sm" onClick={() => openCompare(pick[0], pick[1])}><Icon name="columns" />Compară cele 2 bifate</button>}
        {doc.canWrite && <button className="btn sm primary" onClick={() => setCreating(true)}><Icon name="plus" />Versiune nouă</button>}
      </div>
      {list.length > 1 && pick.length < 2 && <span className="muted small">Bifați două versiuni pentru a le compara, sau folosiți <Icon name="columns" size={12} /> pentru a compara cu versiunea anterioară.</span>}
      <ul className="ver-list">
        {list.map((v, i) => {
          const prev = list[i + 1];
          const k = KIND[v.kind] || KIND.MANUAL;
          const isCur = i === 0;
          const shown = viewingId != null && v.fileId === viewingId && !isCur;
          return (
            <li key={v.id} className={'ver ' + (isCur ? 'current ' : '') + (v.minor === 0 ? 'major' : '')}>
              <label className="ver-pick" title="Bifați pentru comparare"><input type="checkbox" checked={pick.includes(v.id)} onChange={() => togglePick(v.id)} /></label>
              <div className="ver-num">v{v.label}</div>
              <div className="ver-body">
                <div className="ver-head">
                  <span className={'ver-kind ' + v.kind}><Icon name={k.icon} size={11} />{k.label}</span>
                  {isCur && <span className="ver-kind current">curentă</span>}
                  {v.minor === 0 && <span className="ver-kind majorb">majoră</span>}
                  <span className="grow" />
                  <div className="ver-actions">
                    {v.fileId && <button className={'icon-btn' + (shown ? ' on' : '')} title={shown ? 'Se afișează în vizualizator — reveniți la versiunea curentă' : 'Vezi fișierul acestei versiuni'} onClick={() => onView(shown || isCur ? null : v)}><Icon name="eye" size={15} /></button>}
                    {v.fileId && <a className="icon-btn" title="Descarcă" href={api.fileUrl(v.fileId, 'download')}><Icon name="download" size={15} /></a>}
                    {prev && <button className="icon-btn" title={`Compară cu v${prev.label}`} onClick={() => setCompare([prev.id, v.id])}><Icon name="columns" size={15} /></button>}
                    {!isCur && doc.canWrite && <button className="icon-btn" title="Restaurează această versiune" onClick={() => setRestoring(v)}><Icon name="refresh" size={15} /></button>}
                  </div>
                </div>
                <div className="muted small">{v.createdByName} · {fmtDateTime(fixTs(v.createdAt))}</div>
                {v.description && <div className="small">{v.description}</div>}
                <div className="muted small ver-file">
                  {v.fileId ? <><Icon name="file" size={12} />{v.fileName} · {fmtBytes(v.fileSize)}</> : 'fără fișier'}
                  {v.hasText ? <> · text {v.chars.toLocaleString('ro-RO')} caractere</> : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {creating && <NewVersionModal doc={doc} onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); onChanged(); }} />}
      {restoring && <RestoreModal doc={doc} v={restoring} onClose={() => setRestoring(null)} onDone={() => { setRestoring(null); onView(null); load(); onChanged(); }} />}
      {compare && <CompareModal doc={doc} attrs={attrs} versions={list} a={compare[0]} b={compare[1]} onClose={() => setCompare(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------- new version
export function NewVersionModal({ doc, onClose, onDone }: { doc: Doc; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [major, setMajor] = useState(false);
  const [desc, setDesc] = useState('');
  const [file, setFile] = useState<FileMeta | null>(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const upload = async (f: File | undefined) => {
    if (!f) return;
    setUploading(true);
    try { setFile(await api.upload<FileMeta>(f)); } catch (e: any) { toast(e.message, 'error'); } finally { setUploading(false); }
  };
  const save = async () => {
    setBusy(true);
    try {
      const v = await api.post<DocVersion>(`/api/documents/${doc.id}/versions`, { major, description: desc.trim() || null, fileId: file?.id || undefined });
      toast(`Versiunea ${v.label} a fost creată`, 'ok'); onDone();
    } catch (e: any) { toast(e.message, 'error'); } finally { setBusy(false); }
  };
  return (
    <Modal title="Versiune nouă" icon="layers" onClose={onClose}
      footer={<><span className="grow" /><button className="btn" onClick={onClose}>Renunță</button><button className="btn primary" disabled={busy || uploading} onClick={save}><Icon name="check" />Creează v{nextLabel(doc.versionLabel, major)}</button></>}>
      <div className="stack" style={{ gap: 14 }}>
        <Field label="Tipul versiunii">
          <div className="ver-choice">
            <button type="button" className={!major ? 'active' : ''} onClick={() => setMajor(false)}>
              <b>Minoră · v{nextLabel(doc.versionLabel, false)}</b><span className="muted small">Corecturi, completări, o scanare mai bună</span>
            </button>
            <button type="button" className={major ? 'active' : ''} onClick={() => setMajor(true)}>
              <b>Majoră · v{nextLabel(doc.versionLabel, true)}</b><span className="muted small">Variantă finală, semnată, aprobată</span>
            </button>
          </div>
        </Field>
        <Field label="Descriere (opțional)"><textarea className="textarea" rows={2} value={desc} onChange={e => setDesc(e.target.value)} placeholder="ex. Varianta semnată de director" /></Field>
        <Field label="Fișier (opțional)" hint={file ? undefined : 'Fără fișier nou, versiunea păstrează fișierul, textul și datele actuale ale documentului.'}>
          {file ? (
            <div className="row" style={{ gap: 8 }}><Icon name="file" /><span className="grow" style={{ overflowWrap: 'anywhere' }}>{file.name}</span><span className="muted small">{fmtBytes(file.size)}</span><button className="icon-btn" onClick={() => setFile(null)} title="Renunță la fișier"><Icon name="x" size={14} /></button></div>
          ) : (
            <button type="button" className="dropzone sm" disabled={uploading} onClick={() => input.current?.click()} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); upload(e.dataTransfer.files[0]); }}>
              <Icon name="upload" />{uploading ? 'Se încarcă…' : 'Încarcă un fișier nou pentru această versiune'}
            </button>
          )}
          <input ref={input} type="file" hidden onChange={e => upload(e.target.files?.[0])} />
        </Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- restore
function RestoreModal({ doc, v, onClose, onDone }: { doc: Doc; v: DocVersion; onClose: () => void; onDone: () => void }) {
  const toast = useToast();
  const [meta, setMeta] = useState(false);
  const [major, setMajor] = useState(false);
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const r = await api.post<DocVersion>(`/api/documents/${doc.id}/versions/${v.id}/restore`, { metadata: meta, major, description: desc.trim() || null });
      toast(`Versiunea ${v.label} a fost restaurată ca v${r.label}`, 'ok'); onDone();
    } catch (e: any) { toast(e.message, 'error'); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Restaurează versiunea ${v.label}`} icon="refresh" onClose={onClose}
      footer={<><span className="grow" /><button className="btn" onClick={onClose}>Renunță</button><button className="btn primary" disabled={busy} onClick={go}><Icon name="refresh" />Restaurează ca v{nextLabel(doc.versionLabel, major)}</button></>}>
      <div className="stack" style={{ gap: 12 }}>
        <div className="banner small"><Icon name="info" size={15} /><span>Se creează o versiune nouă, identică cu v{v.label}. Versiunile existente rămân neschimbate — puteți reveni oricând.</span></div>
        <div className="small"><b>v{v.label}</b> · {v.fileName || 'fără fișier'} · {v.createdByName} · {fmtDateTime(fixTs(v.createdAt))}{v.description ? <div className="muted">{v.description}</div> : null}</div>
        <Switch on={meta} onChange={setMeta} label="Restaurează și datele documentului (titlul și câmpurile), nu doar fișierul și textul" />
        <Switch on={major} onChange={setMajor} label="Ca versiune majoră" />
        <Field label="Motiv (opțional)"><input className="input" value={desc} onChange={e => setDesc(e.target.value)} placeholder="ex. varianta corectă era cea anterioară" /></Field>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------- compare
type Op = { t: '=' | '-' | '+'; a?: string; b?: string };

/** Myers O(ND) diff on two token lists (lines or words). Returns null when the inputs are too different to be worth it. */
function diffTokens(a: string[], b: string[], maxD = 2500): Op[] | null {
  let pre = 0; while (pre < a.length && pre < b.length && a[pre] === b[pre]) pre++;
  let suf = 0; while (suf < a.length - pre && suf < b.length - pre && a[a.length - 1 - suf] === b[b.length - 1 - suf]) suf++;
  const A = a.slice(pre, a.length - suf), B = b.slice(pre, b.length - suf);
  const N = A.length, M = B.length, max = N + M, off = max + 1;
  const mid: Op[] = [];
  if (N === 0 || M === 0) { A.forEach(x => mid.push({ t: '-', a: x })); B.forEach(x => mid.push({ t: '+', b: x })); }
  else {
    const trace: Int32Array[] = [];
    let v = new Int32Array(2 * max + 3);
    let found = false;
    for (let d = 0; d <= Math.min(max, maxD) && !found; d++) {
      if ((trace.length + 1) * v.length > 24_000_000) return null;   // memory guard (~100 MB)
      trace.push(v.slice());
      for (let k = -d; k <= d; k += 2) {
        let x = k === -d || (k !== d && v[off + k - 1] < v[off + k + 1]) ? v[off + k + 1] : v[off + k - 1] + 1;
        let y = x - k;
        while (x < N && y < M && A[x] === B[y]) { x++; y++; }
        v[off + k] = x;
        if (x >= N && y >= M) { found = true; break; }
      }
    }
    if (!found) return null;
    trace.push(v.slice());
    let x = N, y = M;
    const rev: Op[] = [];
    for (let d = trace.length - 2; d >= 0 && (x > 0 || y > 0); d--) {
      const vv = trace[d], k = x - y;
      const pk = k === -d || (k !== d && vv[off + k - 1] < vv[off + k + 1]) ? k + 1 : k - 1;
      const px = vv[off + pk], py = px - pk;
      while (x > px && y > py) { rev.push({ t: '=', a: A[x - 1], b: B[y - 1] }); x--; y--; }
      if (d > 0) { if (x === px) rev.push({ t: '+', b: B[y - 1] }); else rev.push({ t: '-', a: A[x - 1] }); }
      x = px; y = py;
    }
    while (x > 0 && y > 0) { rev.push({ t: '=', a: A[x - 1], b: B[y - 1] }); x--; y--; }
    mid.push(...rev.reverse());
  }
  return [...a.slice(0, pre).map(x => ({ t: '=' as const, a: x, b: x })), ...mid, ...a.slice(a.length - suf).map(x => ({ t: '=' as const, a: x, b: x }))];
}

/** Word-level highlight inside a changed line pair. */
function WordDiff({ a, b, side }: { a: string; b: string; side: 'a' | 'b' }) {
  const ops = useMemo(() => diffTokens(a.split(/(\s+)/), b.split(/(\s+)/), 400), [a, b]);
  if (!ops) return <>{side === 'a' ? a : b}</>;
  return <>{ops.map((o, i) => o.t === '=' ? <span key={i}>{o.a}</span> : (side === 'a' && o.t === '-') ? <mark key={i} className="wd-del">{o.a}</mark> : (side === 'b' && o.t === '+') ? <mark key={i} className="wd-add">{o.b}</mark> : null)}</>;
}

type Row = { kind: 'same' | 'del' | 'add' | 'chg' | 'gap'; a?: string; b?: string; na?: number; nb?: number; hidden?: number };

function textRows(ta: string, tb: string, context = 3): { rows: Row[]; added: number; removed: number; tooBig: boolean } {
  const lines = (t: string) => { const x = t.replace(/\r/g, '').replace(/\n+$/, ''); return x ? x.split('\n') : []; };
  const la = lines(ta), lb = lines(tb);
  const ops = diffTokens(la, lb);
  if (!ops) return { rows: [], added: lb.length, removed: la.length, tooBig: true };
  // pair consecutive deletions and additions as "changed" lines
  const rows: Row[] = [];
  let na = 0, nb = 0, added = 0, removed = 0;
  for (let i = 0; i < ops.length;) {
    if (ops[i].t === '=') { na++; nb++; rows.push({ kind: 'same', a: ops[i].a, b: ops[i].b, na, nb }); i++; continue; }
    const dels: string[] = [], adds: string[] = [];
    while (i < ops.length && ops[i].t !== '=') { if (ops[i].t === '-') dels.push(ops[i].a!); else adds.push(ops[i].b!); i++; }
    removed += dels.length; added += adds.length;
    for (let j = 0; j < Math.max(dels.length, adds.length); j++) {
      const a = dels[j], b = adds[j];
      if (a != null) na++; if (b != null) nb++;
      rows.push({ kind: a != null && b != null ? 'chg' : a != null ? 'del' : 'add', a, b, na: a != null ? na : undefined, nb: b != null ? nb : undefined });
    }
  }
  // collapse long unchanged stretches
  const keep = rows.map(() => false);
  rows.forEach((r, i) => { if (r.kind !== 'same') for (let j = Math.max(0, i - context); j <= Math.min(rows.length - 1, i + context); j++) keep[j] = true; });
  const out: Row[] = [];
  for (let i = 0; i < rows.length;) {
    if (keep[i] || rows[i].kind !== 'same') { out.push(rows[i]); i++; continue; }
    let j = i; while (j < rows.length && !keep[j] && rows[j].kind === 'same') j++;
    out.push({ kind: 'gap', hidden: j - i }); i = j;
  }
  return { rows: out, added, removed, tooBig: false };
}

export function CompareModal({ doc, attrs, versions, a, b, onClose }: { doc: Doc; attrs: Attribute[]; versions: DocVersion[]; a: number; b: number; onClose: () => void }) {
  const toast = useToast();
  const [ida, setIda] = useState(a);
  const [idb, setIdb] = useState(b);
  const [va, setVa] = useState<DocVersion | null>(null);
  const [vb, setVb] = useState<DocVersion | null>(null);
  const [tab, setTab] = useState<'text' | 'data' | 'files'>('text');
  const [full, setFull] = useState(false);
  useEffect(() => { setVa(null); api.get<DocVersion>(`/api/documents/${doc.id}/versions/${ida}`).then(setVa).catch(e => toast(e.message, 'error')); }, [ida]);
  useEffect(() => { setVb(null); api.get<DocVersion>(`/api/documents/${doc.id}/versions/${idb}`).then(setVb).catch(e => toast(e.message, 'error')); }, [idb]);
  const diff = useMemo(() => va && vb ? textRows(va.text || '', vb.text || '', full ? 1e9 : 3) : null, [va, vb, full]);
  const dataRows = useMemo(() => {
    if (!va || !vb) return [];
    const rows: { label: string; a: React.ReactNode; b: React.ReactNode; changed: boolean }[] = [];
    const push = (label: string, x: any, y: any, render: (v: any) => React.ReactNode = v => v == null || v === '' ? <span className="muted">—</span> : String(v)) =>
      rows.push({ label, a: render(x), b: render(y), changed: JSON.stringify(x ?? null) !== JSON.stringify(y ?? null) });
    push('Titlu', va.title, vb.title);
    push('Fișier', va.fileName, vb.fileName);
    push('Mărime', va.fileSize, vb.fileSize, v => v == null ? <span className="muted">—</span> : fmtBytes(v));
    const codes = new Set([...attrs.map(x => x.code), ...Object.keys(va.metadata || {}), ...Object.keys(vb.metadata || {})]);
    for (const code of codes) {
      const at = attrs.find(x => x.code === code);
      const x = (va.metadata || {})[code], y = (vb.metadata || {})[code];
      if ((x == null || x === '') && (y == null || y === '')) continue;
      push(at?.name || code, x, y, v => v == null || v === '' ? <span className="muted">—</span> : at ? renderValue(at, v) : String(v));
    }
    return rows;
  }, [va, vb, attrs]);
  const changedData = dataRows.filter(r => r.changed).length;
  const sel = (value: number, set: (n: number) => void) => (
    <select className="select" value={value} onChange={e => set(Number(e.target.value))}>
      {versions.map(v => <option key={v.id} value={v.id}>v{v.label} · {KIND[v.kind]?.label} · {fmtDateTime(fixTs(v.createdAt))}</option>)}
    </select>
  );
  return (
    <Modal title="Compară versiunile" icon="columns" size="xl" onClose={onClose}>
      <div className="cmp-head">
        <div className="cmp-side a"><span className="cmp-tag">A</span>{sel(ida, setIda)}{va && <span className="muted small">{va.createdByName}{va.description ? ` · ${va.description}` : ''}</span>}</div>
        <button className="icon-btn" title="Inversează" onClick={() => { setIda(idb); setIdb(ida); }}><Icon name="arrowRight" /></button>
        <div className="cmp-side b"><span className="cmp-tag">B</span>{sel(idb, setIdb)}{vb && <span className="muted small">{vb.createdByName}{vb.description ? ` · ${vb.description}` : ''}</span>}</div>
      </div>
      <div className="tabs" style={{ margin: '12px 0' }}>
        <button className={tab === 'text' ? 'active' : ''} onClick={() => setTab('text')}><Icon name="scan" size={15} />Text{diff && !diff.tooBig && (diff.added || diff.removed) ? ` (+${diff.added} −${diff.removed})` : ''}</button>
        <button className={tab === 'data' ? 'active' : ''} onClick={() => setTab('data')}><Icon name="list" size={15} />Date{changedData ? ` (${changedData} diferite)` : ''}</button>
        <button className={tab === 'files' ? 'active' : ''} onClick={() => setTab('files')}><Icon name="columns" size={15} />Fișiere alăturate</button>
      </div>
      {!va || !vb ? <p className="muted">Se încarcă…</p> : tab === 'text' ? (
        !va.hasText && !vb.hasText ? <p className="muted">Niciuna dintre versiuni nu are text recunoscut. Apăsați <b>OCR</b> pe document pentru a crea o versiune cu text.</p>
        : diff!.tooBig ? <p className="muted">Textele sunt prea diferite pentru o comparație rând cu rând — folosiți „Fișiere alăturate”.</p>
        : diff!.added === 0 && diff!.removed === 0 ? <div className="banner small"><Icon name="check" size={15} /><span>Textul este identic în cele două versiuni.</span></div>
        : (
          <>
            <div className="row small" style={{ gap: 12, marginBottom: 8 }}>
              <span><mark className="wd-del">text eliminat</mark> în A</span><span><mark className="wd-add">text adăugat</mark> în B</span><span className="grow" />
              <label className="checkbox"><input type="checkbox" checked={full} onChange={e => setFull(e.target.checked)} /><span>Arată tot textul</span></label>
            </div>
            <div className="sbs">
              {diff!.rows.map((r, i) => r.kind === 'gap'
                ? <div key={i} className="sbs-gap" onClick={() => setFull(true)}>⋯ {r.hidden} {r.hidden === 1 ? 'rând identic' : 'rânduri identice'} — clic pentru a le arăta</div>
                : (
                  <div key={i} className={'sbs-row ' + r.kind}>
                    <span className="ln">{r.na ?? ''}</span>
                    <div className="c a">{r.a == null ? null : r.kind === 'chg' ? <WordDiff a={r.a} b={r.b!} side="a" /> : r.a}</div>
                    <span className="ln">{r.nb ?? ''}</span>
                    <div className="c b">{r.b == null ? null : r.kind === 'chg' ? <WordDiff a={r.a!} b={r.b} side="b" /> : r.b}</div>
                  </div>
                ))}
            </div>
          </>
        )
      ) : tab === 'data' ? (
        <table className="table cmp-data">
          <thead><tr><th style={{ width: 200 }}>Câmp</th><th>A · v{va.label}</th><th>B · v{vb.label}</th></tr></thead>
          <tbody>{dataRows.map((r, i) => <tr key={i} className={r.changed ? 'changed' : ''}><th>{r.label}</th><td>{r.a}</td><td>{r.b}</td></tr>)}</tbody>
        </table>
      ) : (
        <div className="cmp-files">
          {[va, vb].map((v, i) => (
            <div key={i} className="cmp-file">
              <div className="viewbar"><span className="cmp-tag">{i ? 'B' : 'A'}</span><span className="grow" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>v{v.label} · {v.fileName || 'fără fișier'}</span>
                {v.fileId && <a className="btn sm ghost" href={api.fileUrl(v.fileId, 'download')}><Icon name="download" />Descarcă</a>}</div>
              {!v.fileId ? <div className="nofile"><Icon name="file" size={30} /><p>Fără fișier</p></div>
                : (v.fileMime || '').startsWith('image/') ? <img src={api.fileUrl(v.fileId, 'raw')} alt={v.fileName || ''} />
                : <iframe title={'v' + v.label} src={api.fileUrl(v.fileId, 'preview')} />}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
